import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Notify from "../components/Notify";
import { useNotifications } from "../context/NotificationContext";
import { notificationService, restaurantOrderService } from "../services";
import {
  NotificationItem,
  isNotificationUnread,
} from "../utils/types";

const PAGE_LIMIT = 20;

interface DateSection {
  label: string;
  items: NotificationItem[];
}

const getDateSectionLabel = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const notifDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (notifDate.getTime() === today.getTime()) return "TODAY";
  if (notifDate.getTime() === yesterday.getTime()) return "YESTERDAY";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const buildSections = (items: NotificationItem[]): DateSection[] => {
  const map = new Map<string, NotificationItem[]>();

  items.forEach((item) => {
    const label = getDateSectionLabel(item.createdAt);
    const existing = map.get(label) || [];
    existing.push(item);
    map.set(label, existing);
  });

  return Array.from(map.entries()).map(([label, sectionItems]) => ({
    label,
    items: sectionItems,
  }));
};

const Notification = () => {
  const navigate = useNavigate();
  const { unreadCount, setUnreadCount, refreshUnreadCount } =
    useNotifications();
  const isFetchingRef = useRef(false);

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchNotifications = useCallback(
    async (pageNum: number) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const res = await notificationService.getNotifications(
          pageNum,
          PAGE_LIMIT
        );
        if (res.status && res.data) {
          const newNotifications = res.data;
          const pagination = res.pagination;
          const totalPages = pagination?.totalPages || 1;

          setNotifications((prev) =>
            pageNum === 1 ? newNotifications : [...prev, ...newNotifications]
          );
          setHasMore(pageNum < totalPages);
          setPage(pageNum);

          if (pagination?.unreadCount !== undefined) {
            setUnreadCount(pagination.unreadCount);
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch notifications:", error);
        toast.error(error.message || "Failed to load notifications");
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [setUnreadCount]
  );

  useEffect(() => {
    fetchNotifications(1);
    refreshUnreadCount();
  }, [fetchNotifications, refreshUnreadCount]);

  const handleMarkAllRead = async () => {
    if (markingAllRead || unreadCount === 0) return;

    setMarkingAllRead(true);
    try {
      const res: any = await notificationService.markAllAsRead();
      if (res.status) {
        const readAt = new Date().toISOString();
        setNotifications((prev) => prev.map((item) => ({ ...item, readAt })));
        setUnreadCount(0);
      }
    } catch (error: any) {
      console.error("Failed to mark all as read:", error);
      toast.error(error.message || "Failed to mark notifications as read");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const markNotificationAsRead = async (notification: NotificationItem) => {
    if (!isNotificationUnread(notification)) return;

    const readAt = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((item) =>
        item._id === notification._id ? { ...item, readAt } : item
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await notificationService.markAsRead(notification._id);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === notification._id ? { ...item, readAt: null } : item
        )
      );
      setUnreadCount((prev) => prev + 1);
    }
  };

  const handleNotificationPress = async (notification: NotificationItem) => {
    await markNotificationAsRead(notification);

    const orderId = notification.data?.orderId;
    if (!orderId) return;

    try {
      const response: any = await restaurantOrderService.getOrderById(orderId);
      if (response.status && response.data) {
        navigate(`/orders?orderId=${orderId}`);
        return;
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    }

    navigate("/orders");
  };

  const handleLoadMore = () => {
    if (!loadingMore && !loading && hasMore) {
      fetchNotifications(page + 1);
    }
  };

  const sections = buildSections(notifications);

  return (
    <div className="p-5 bg-white dark:bg-gray-900 min-h-screen transition-colors pb-24 md:pb-5">
      <div className="max-w-4xl w-full md:w-[800px] mx-auto">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] md:text-[24px] font-semibold">
            Notifications
          </h1>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markingAllRead}
              className="text-[#FF8A00] font-Urbanist text-[14px] font-semibold disabled:opacity-60"
            >
              {markingAllRead ? "Updating..." : "Mark all read"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF8A00]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              className="text-[#C4C4C4] dark:text-gray-600 mb-4"
              aria-hidden
            >
              <path
                d="M13.73 21a2 2 0 01-3.46 0"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M18.63 13C18.87 14.31 18.29 15.77 17.19 16.87 16.07 17.99 14.07 18.31 13 18.31H8.5C6.01 18.31 4.5 16.41 4.5 14.5V9.13C4.5 5.59 7.59 2.91 11.13 3.13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M18.75 4.25l-6.5 6.5M18.75 10.75l-6.5-6.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-semibold mb-1">
              No notifications yet
            </p>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[14px]">
              Order updates and alerts will appear here
            </p>
          </div>
        ) : (
          <>
            {sections.map((section) => (
              <div key={section.label}>
                <h6 className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[16px] font-medium leading-[140%] mb-[12px] transition-colors">
                  {section.label}
                </h6>
                {section.items.map((item) => (
                  <Notify
                    key={item._id}
                    notification={item}
                    onPress={handleNotificationPress}
                  />
                ))}
              </div>
            ))}

            {hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 rounded-lg bg-[#F5F5F5] dark:bg-gray-700 text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] disabled:opacity-60"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notification;
